import "./VoiceModeVisual.css";
import orbieeVoiceLogo from "../../images/brand/orbiee-mark.svg";

/**
 * Resolve visual mode for the voice visual.
 * Priority: muted → idle; recording → listening; loading → thinking; speaking → speaking; else idle.
 */
export function resolveVoiceVisualMode({
  isMicMuted = false,
  isRecording = false,
  isLoading = false,
  isSpeaking = false,
}) {
  if (isMicMuted) return "idle";
  if (isRecording) return "listening";
  if (isLoading) return "thinking";
  if (isSpeaking) return "speaking";
  return "idle";
}

/** What the orb says it is doing, per state. */
const CAPTION = {
  idle: ["Voice paused", "tap the mic to speak"],
  listening: ["Listening", ""],
  thinking: ["Thinking", ""],
  speaking: ["Speaking", ""],
};

/**
 * The voice orb: a state-coloured core inside a soft glow and a halo that
 * breathes while listening and spins while thinking.
 *
 * The core scales with `audioLevel`, and the level meter below it is driven
 * by `transform: scaleX()` rather than `width` — this repaints many times a
 * second on the same main thread that schedules the reply's audio buffers,
 * and a width change would relayout the panel on every one of them.
 *
 * Visual only: it reads state and never changes it.
 */
export default function VoiceModeVisual({
  audioLevel = 0,
  isRecording = false,
  isLoading = false,
  isMicMuted = false,
  isSpeaking = false,
}) {
  const mode = resolveVoiceVisualMode({
    isMicMuted,
    isRecording,
    isLoading,
    isSpeaking,
  });

  // While the agent speaks the mic is muted, so there is no real level to
  // show; the orb breathes on its own rather than reporting a flat zero that
  // would read as "it stopped working".
  const amp = isRecording
    ? Math.min((Number(audioLevel) || 0) * 14, 1)
    : mode === "speaking"
      ? 0.55
      : mode === "thinking"
        ? 0.34
        : 0.16;

  const [label, hint] = CAPTION[mode] || CAPTION.idle;

  return (
    <div className="voice-mode-visual" data-mode={mode}>
      <div className="voice-mode-visual__stage">
        <div
          className="ob-orb voice-orb"
          data-vstate={mode === "idle" ? "off" : mode}
          style={{ "--ob-amp": amp.toFixed(3) }}
          aria-hidden="true"
        >
          <span className="ob-orb-glow" />
          <span className="ob-orb-halo" />
          <span className="ob-orb-halo voice-orb__halo-2" />
          <span className="ob-orb-core">
            <img
              className="voice-orb__logo"
              src={orbieeVoiceLogo}
              alt=""
              draggable={false}
            />
          </span>
        </div>

        <div className="voice-orb__caption" role="status" aria-live="polite">
          <span className="voice-orb__label">{label}</span>
          <span className="voice-orb__hint">{hint}</span>
        </div>

        <div className="ob-meter voice-orb__meter">
          <span style={{ transform: `scaleX(${amp.toFixed(3)})` }} />
        </div>
      </div>
    </div>
  );
}
